import React from 'react';
import {
    ArrayInput,
    Create,
    Edit,
    FormDataConsumer,
    SimpleForm,
    SimpleFormIterator,
    TextInput,
    useGetIdentity,
    usePermissions,
    useRecordContext,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Alert, Box, Typography} from '@mui/material';
import {DeleteOutline} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useUmbrellaChoices} from '../lib/customHooks';
import hasPermission from '../auth/hasPermission';

const ENTITY_TYPE_CHOICES = [
    {id: 'CHILD',    name: 'Child'},
    {id: 'GUARDIAN', name: 'Guardian'},
    {id: 'CHARITY',  name: 'Charity'},
];

const FIELD_TYPE_CHOICES = [
    {id: 'TEXT',    name: 'Text'},
    {id: 'NUMBER',  name: 'Number'},
    {id: 'DATE',    name: 'Date'},
    {id: 'BOOLEAN', name: 'Boolean'},
    {id: 'SELECT',  name: 'Select'},
];

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const HeaderBar = ({mode, record, translate, canDelete, onDelete}) => {
    const title = record?.field_name || translate('Create definition');
    const statusVariant = record?.is_active ? 'success' : 'neutral';

    return (
        <PageHeader
            breadcrumb={{label: translate('Metadata Definitions'), to: '/metadata-definitions'}}
            title={
                mode === 'create' ? (
                    <Box component="span">{translate('Create definition')}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant}/>
                        <Typography component="span" sx={{fontFamily: t.font.mono, fontSize: `${t.type.display.size}px`, fontWeight: t.type.display.weight, lineHeight: t.type.display.lineHeight}}>
                            {title}
                        </Typography>
                    </Box>
                )
            }
            actions={
                <>
                    <SaveAction label={mode === 'create' ? translate('Create definition') : translate('Save changes')}/>
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const umbrellaChoices = useUmbrellaChoices();
    const deleteConfirm = useDeleteConfirm();

    const canSeeUmbrella = !!hasPermission(permissions, ['view'], 'umbrella');
    const canDelete = !!permissions && (permissions.includes?.('admin') || permissions.includes?.('umbrella'));
    const isEdit = mode === 'edit';

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canDelete={canDelete}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="metadata-definitions"
                    record={record}
                    recordLabel={record.field_name}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Basic Information')}>
                        {canSeeUmbrella && umbrellaChoices && (
                            <Box sx={{mb: 2}}>
                                <StatamicSelectInput
                                    source="parent_charity_id"
                                    label={translate('Umbrella')}
                                    required
                                    choices={umbrellaChoices}
                                />
                            </Box>
                        )}
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicSelectInput
                                source="entity_type"
                                label={translate('Entity Type')}
                                required
                                choices={ENTITY_TYPE_CHOICES}
                            />
                            <StatamicSelectInput
                                source="field_type"
                                label={translate('Field Type')}
                                required
                                choices={FIELD_TYPE_CHOICES}
                            />
                        </Box>
                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput
                                source="field_name"
                                label={translate('Field Name (Internal)')}
                                helperText={isEdit
                                    ? translate('Cannot be changed after creation')
                                    : translate('Auto-generated from label if left empty')}
                                readOnly={isEdit}
                            />
                            <StatamicTextInput
                                source="field_label"
                                label={translate('Field Label')}
                                helperText={translate('Enter the display name for this field')}
                                required
                            />
                        </Box>
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput
                                source="display_order"
                                label={translate('Display Order')}
                                type="number"
                                defaultValue={0}
                            />
                        </Box>

                        <FormDataConsumer>
                            {({formData}) => formData.field_type === 'SELECT' && (
                                <Box sx={{mt: 2, pt: 2, borderTop: `1px solid ${t.color.border.hairline}`}}>
                                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500, mb: 1}}>
                                        {translate('Options')}
                                    </Typography>
                                    <ArrayInput source="field_options" label="">
                                        <SimpleFormIterator inline>
                                            <TextInput label={translate('Option')} helperText={false}/>
                                        </SimpleFormIterator>
                                    </ArrayInput>
                                </Box>
                            )}
                        </FormDataConsumer>
                    </SectionCard>

                    <SectionCard title={translate('Charity Permissions')}>
                        <Alert severity="info" sx={{mb: 2, fontSize: `${t.type.small.size}px`}}>
                            {translate('Control what charity admins can do with this field')}
                        </Alert>
                        <ToggleRow
                            source="charity_can_view"
                            label={translate('Charity Can View')}
                            subtitle="Charity admins will see this field in show pages"
                            defaultValue={!isEdit}
                        />
                        <ToggleRow
                            source="charity_can_edit"
                            label={translate('Charity Can Edit')}
                            subtitle="Charity admins can modify this field"
                            defaultValue={false}
                        />
                    </SectionCard>

                    <FormDataConsumer>
                        {({formData}) => formData.entity_type === 'GUARDIAN' && (
                            <SectionCard title={translate('Registration form')}>
                                <ToggleRow
                                    source="show_in_registration"
                                    label={translate('Show in Registration')}
                                    subtitle={translate('Display this field during sponsor registration')}
                                    defaultValue={false}
                                />
                                {formData.show_in_registration && (
                                    <Box sx={{mt: 2}}>
                                        <StatamicTextInput
                                            source="field_description"
                                            label={translate('Field Description')}
                                            helperText={translate('Description shown to sponsors during registration')}
                                            multiline
                                            rows={3}
                                        />
                                    </Box>
                                )}
                            </SectionCard>
                        )}
                    </FormDataConsumer>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="is_active"
                            label={translate('Active')}
                            subtitle="Inactive fields are hidden from all forms"
                            defaultValue={true}
                        />
                        <ToggleRow
                            source="is_required"
                            label={translate('Required Field')}
                            subtitle="Forms cannot be submitted without a value"
                            defaultValue={false}
                        />
                        <ToggleRow
                            source="show_in_list"
                            label={translate('Show in List View')}
                            subtitle="Include this field as a column in list screens"
                            defaultValue={true}
                        />
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

const transformIncoming = (data) => {
    if (data && data.parent_charity_id !== undefined) {
        return {...data, umbrella_id: data.parent_charity_id};
    }
    return data;
};

const transformOutgoingCreate = (identity, permissions) => (data) => {
    const out = {...data};
    if (permissions?.includes?.('umbrella') && !permissions?.includes?.('admin') && identity?.charityId) {
        out.umbrella_id = identity.charityId;
    }
    return out;
};

export const MetadataDefinitionEditStatamic = (props) => (
    <Edit
        {...props}
        actions={false}
        mutationMode="pessimistic"
        component={Box}
        redirect="show"
        transform={transformIncoming}
    >
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const MetadataDefinitionCreateStatamic = (props) => {
    const {permissions} = usePermissions();
    const {data: identity} = useGetIdentity();
    return (
        <Create
            {...props}
            actions={false}
            mutationMode="pessimistic"
            component={Box}
            redirect="show"
            transform={transformOutgoingCreate(identity, permissions)}
        >
            <SimpleForm toolbar={false}>
                <FormBody mode="create"/>
            </SimpleForm>
        </Create>
    );
};

export default MetadataDefinitionEditStatamic;
